import { tagged } from "@mirage/service-logging";

import type { dash } from "@dropbox/api-v2-client";
import type { ChannelForFilter } from "@mirage/shared/search/search-filters";
import type { APIv2Callable } from "..";

const logger = tagged("channel-recommendations");

export async function getChannelRecommendations(
  api: APIv2Callable,
): Promise<ChannelForFilter[]> {
  const channelFilter: dash.QueryFilter = {
    filter: {
      ".tag": "file_type_filter",
      file_type: "channel",
    },
  };
  try {
    const response = await api("dcsBrowse", {
      filters: [channelFilter],
    });
    if (!response.results) {
      return [];
    }
    const channels = response.results
      ?.map((result) => {
        if (result.logical_type !== "channel") {
          return null;
        }
        return transformSearchResultChannelToMirageChannel(result);
      })
      .filter((channelResult): channelResult is ChannelForFilter =>
        Boolean(channelResult),
      );
    return channels;
  } catch (error) {
    logger.error(`Error getting channel recommendations`, error);
    return [];
  }
}

export async function getChannelSearchResults(
  api: APIv2Callable,
  query: string,
): Promise<ChannelForFilter[]> {
  const channelFilter: dash.QueryFilter = {
    filter: {
      ".tag": "file_type_filter",
      file_type: "channel",
    },
  };
  try {
    const response = await api("dcsSearch", {
      query_text: query,
      filters: [channelFilter],
    });
    if (!response.results) {
      return [];
    }
    const channels = response.results
      .map((result) => {
        if (
          result.query_result?.[".tag"] !== "search_result" ||
          result.query_result.logical_type !== "channel"
        ) {
          return null;
        }
        return transformSearchResultChannelToMirageChannel(result.query_result);
      })
      .filter((channelResult): channelResult is ChannelForFilter =>
        Boolean(channelResult),
      );
    return channels;
  } catch (error) {
    logger.error(`Error getting channel search results`, error);
    return [];
  }
}

const transformSearchResultChannelToMirageChannel = (
  searchResult: dash.SearchResult,
): ChannelForFilter | null => {
  if (!searchResult.uuid || !searchResult.title) {
    return null;
  }
  return {
    uuid: searchResult.uuid,
    name: searchResult.title,
    brandedType: searchResult.branded_type || "",
    // channel field is not currently populated on SearchResult response
    // so expect all channels to display generically for now
    type: searchResult.channel?.type || {
      ".tag": "channel_type_unset",
    },
  };
};
